"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@clerk/nextjs";
import { ConvexQueryCacheProvider } from "convex-helpers/react/cache/provider";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { type ReactNode, useCallback, useMemo } from "react";
import { fetchTokenWithRetry } from "@/lib/auth/token-retry";
import { capturePosthogEvent } from "@/lib/posthog";
if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
  throw new Error("Missing NEXT_PUBLIC_CONVEX_URL in your .env file");
}
const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL);

/**
 * Clerk `useAuth` whose `getToken` retries a failed or empty token while signed in.
 * The Convex adapter treats any token failure as signed out, which unmounts every org page
 * (see `EnforceOrganization`). A brief failure, e.g. the first refresh after the laptop wakes,
 * should not do that.
 */
function useAuthWithTokenRetry() {
  const $ = _c(6);
  const auth = useAuth();
  const {
    getToken,
    isSignedIn
  } = auth;
  let t0;
  if ($[0] !== getToken || $[1] !== isSignedIn) {
    t0 = options => fetchTokenWithRetry(() => getToken(options), {
      isSignedIn: Boolean(isSignedIn),
      onRetried: _temp
    });
    $[0] = getToken;
    $[1] = isSignedIn;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const getTokenWithRetry = t0;
  let t1;
  if ($[3] !== auth || $[4] !== getTokenWithRetry) {
    t1 = {
      ...auth,
      getToken: getTokenWithRetry
    };
    $[3] = auth;
    $[4] = getTokenWithRetry;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp(result) {
  return capturePosthogEvent("AgroProj:Auth:ConvexTokenRetried", result);
}
export default function ConvexClientProvider({
  children
}: {
  children: ReactNode;
}) {
  return <ConvexProviderWithClerk client={convex} useAuth={useAuthWithTokenRetry}>
      {/* Idle subscriptions expire after 5 minutes; retain at most 50 for instant revisits. */}
      <ConvexQueryCacheProvider maxIdleEntries={50}>{children}</ConvexQueryCacheProvider>
    </ConvexProviderWithClerk>;
}